/**
 * Organization Users — resource-scoped runtime hooks.
 *
 * These pair the canonical admin hub resource contract for organization-users
 * with the concrete list/read hooks used by generic resource pages.
 */
import { ADMIN_RESOURCE_REGISTRY, type OperationId, type RouteId } from '@finance/contracts';
import { useOrganizationUsersList as useOrganizationUsersListQuery, type UserSurfaceDto } from '../../../api/generated/client';
import { useUser } from '../../admin/admin-resource-hooks.js';
import { selectApiData, type ApiSuccessEnvelope } from '../../../api/select-api-data.js';
import type { ListHookResult, GetHookResult, ResourceHookBinding } from '../../resource-hook-registry.js';

const contract = ADMIN_RESOURCE_REGISTRY['organization-users'];

function selectUserListRows(response: unknown): UserSurfaceDto[] {
  const dto = selectApiData(response as ApiSuccessEnvelope<{ data: { table: { rows: UserSurfaceDto[] } }, page: number; totalPages: number; total: number }>);
  return dto.data.table.rows;
}

function useUserList(): ListHookResult<UserSurfaceDto[]> {
  return useOrganizationUsersListQuery(undefined, { query: { select: selectUserListRows } });
}

export const ORGANIZATION_USERS_HOOKS: ResourceHookBinding = {
  useList: useUserList,
  selectList: selectUserListRows,
  useGet: (id: string) => useUser(id) as unknown as GetHookResult<unknown>,
  selectDetail: (data) => data,
  rowIdExtractor: (row: Record<string, unknown>) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'userId',
  paramName: 'userId',
  listRouteId: contract.listRouteId,
  detailRouteId: (contract.detailRouteId ?? contract.listRouteId) as RouteId,
  readOperationId: contract.readOperationId as OperationId,
};
